import { FC, useEffect } from 'react';
import { Redirect } from 'react-router';
import { Feature } from '@shinetools/pricing-plan-library';

import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import Loader from 'components/Loader';
import Stepper from 'components/Stepper';
import { StepOptions } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';

import { BulkTransferContextProvider, useBulkTransferContext } from './context';
import BulkTransfersCreationSuccess from './steps/BulkTransfersCreationSuccess';
import BulkTransfersFileUpload from './steps/BulkTransfersFileUpload';
import BulkTransfersList from './steps/BulkTransfersList';
import { BulkTransferSharedState } from './types';

const BulkTransfers: FC = () => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  const { isFeatureAvailable } = useCurrentPricingPlan();
  const { loading: isLoading, mainBankAccountId } = useMainBankAccountId();

  const { setSharedState } = useBulkTransferContext();

  useEffect(() => {
    if (!mainBankAccountId || !companyUserId || !companyProfileId) {
      return;
    }
    setSharedState((sharedState: BulkTransferSharedState) => ({
      ...sharedState,
      bankAccountId: mainBankAccountId,
      companyProfileId,
      companyUserId,
    }));
  }, [mainBankAccountId, companyProfileId, companyUserId, setSharedState]);

  if (isLoading) {
    return <Loader />;
  }

  if (!isFeatureAvailable(Feature.BulkTransfers)) {
    return <Redirect to="/payments/transfers" />;
  }

  const steps: StepOptions[] = [
    {
      key: 'file-upload',
      render: (props) => (
        <>
          <TrackView pageName="BulkTransfersFileUpload" />
          <BulkTransfersFileUpload {...props} />
        </>
      ),
    },
    {
      key: 'transfers-list',
      render: (props) => (
        <>
          <TrackView pageName="BulkTransfersList" />
          <BulkTransfersList {...props} />
        </>
      ),
    },
    {
      key: 'creation-success',
      render: (props) => (
        <>
          <TrackView pageName="BulkTransfersCreationSuccess" />
          <BulkTransfersCreationSuccess {...props} />
        </>
      ),
    },
  ];

  return <Stepper steps={steps} />;
};

const Container: FC = () => (
  <BulkTransferContextProvider>
    <BulkTransfers />
  </BulkTransferContextProvider>
);

export default Container;
